import type {
    ExerciseCard,
    ExerciseDetail,
    Faq,
    Pillar,
    PostCard,
    PostDetail
} from '@/types'

import { siteConfig } from '@/constants/site'
import { brand } from '@/constants/brand'

type Json = Record<string, unknown>

const SITE = siteConfig.url.replace(/\/$/, '')

export const absoluteUrl = (path?: string): string => {
    if (!path) return SITE
    if (path.startsWith('http')) return path
    return `${SITE}${path.startsWith('/') ? '' : '/'}${path}`
}

const ORG_ID = `${SITE}/#organization`
const WEBSITE_ID = `${SITE}/#website`

// Site-wide publisher. Referenced by @id everywhere else.
export const organizationSchema = (): Json => ({
    '@type': 'Organization',
    '@id': ORG_ID,
    name: siteConfig.name,
    url: SITE,
    email: brand.email,
    description: siteConfig.description,
    logo: {
        '@type': 'ImageObject',
        url: absoluteUrl('/logo.svg'),
        contentUrl: absoluteUrl('/logo.svg')
    },
    knowsAbout: [
        'Holistic health',
        'Lifestyle balance',
        'Mind-body practices',
        'Nutrition',
        'Sleep',
        'Movement',
        'Stress management'
    ]
})

// Site-wide WebSite node.
export const websiteSchema = (): Json => ({
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE,
    name: siteConfig.name,
    description: siteConfig.description,
    inLanguage: 'en-US',
    publisher: { '@id': ORG_ID }
})

export type Crumb = {
    name: string
    path: string
}

export const breadcrumbSchema = (crumbs: Crumb[]): Json => ({
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: crumb.name,
        item: absoluteUrl(crumb.path)
    }))
})

export type FaqItem = Faq

export const faqSchema = (items: FaqItem[]): Json => ({
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer
        }
    }))
})

const wordCount = (html: string): number => {
    const text = html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&[a-z]+;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    if (!text) return 0
    return text.split(/\s+/).length
}

// Authorship is deliberately the Organization, never a Person. WellPath Fusion
// publishes general wellness education with no named clinical reviewer, so
// claiming an individual expert author would be an unearned trust signal.
export const articleSchema = (post: PostDetail): Json => {
    const pageUrl = absoluteUrl(`/${post.pillarSlug}/${post.slug}`)
    const image = post.coverImage ? absoluteUrl(post.coverImage) : absoluteUrl(siteConfig.ogImage)

    return {
        '@type': 'Article',
        '@id': `${pageUrl}#article`,
        isPartOf: { '@id': WEBSITE_ID },
        mainEntityOfPage: pageUrl,
        url: pageUrl,
        headline: post.title.slice(0, 110),
        description: post.excerpt,
        image: [image],
        datePublished: post.publishedAt,
        dateModified: post.updatedAt,
        articleSection: post.pillarName,
        keywords: post.tags.join(', '),
        wordCount: wordCount(post.body),
        timeRequired: `PT${post.readMinutes}M`,
        inLanguage: 'en-US',
        isAccessibleForFree: true,
        author: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID }
    }
}

// The pillar hub is itself a substantial original guide, so it carries an
// Article node plus an ItemList pointing at every cluster article beneath it.
export const pillarSchema = (pillar: Pillar, posts: PostCard[], updatedAt: string): Json => {
    const pageUrl = absoluteUrl(`/${pillar.slug}`)
    const image = pillar.coverImage
        ? absoluteUrl(pillar.coverImage)
        : absoluteUrl(siteConfig.ogImage)

    return {
        '@type': 'Article',
        '@id': `${pageUrl}#article`,
        isPartOf: { '@id': WEBSITE_ID },
        mainEntityOfPage: pageUrl,
        url: pageUrl,
        headline: pillar.h1.slice(0, 110),
        description: pillar.description,
        image: [image],
        dateModified: updatedAt,
        articleSection: pillar.navGroupName ?? pillar.name,
        wordCount: wordCount(pillar.body ?? ''),
        inLanguage: 'en-US',
        isAccessibleForFree: true,
        author: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID },
        hasPart: posts.map((post) => ({
            '@type': 'Article',
            url: absoluteUrl(`/${post.pillarSlug}/${post.slug}`),
            headline: post.title
        }))
    }
}

// Derives HowTo steps from the first ordered list in a step-by-step article.
// Returns null when the body has no ordered list, so we never emit an empty or
// invented HowTo graph.
export const howToSchema = (post: PostDetail): Json | null => {
    const list = /<ol[^>]*>([\s\S]*?)<\/ol>/i.exec(post.body)
    if (!list) return null

    const steps = [...list[1].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
        .map((match) =>
            match[1]
                .replace(/<[^>]+>/g, ' ')
                .replace(/&nbsp;/gi, ' ')
                .replace(/\s+/g, ' ')
                .trim()
        )
        .filter(Boolean)

    if (steps.length < 2) return null

    const pageUrl = absoluteUrl(`/${post.pillarSlug}/${post.slug}`)

    return {
        '@type': 'HowTo',
        '@id': `${pageUrl}#howto`,
        name: post.title,
        description: post.excerpt,
        totalTime: `PT${post.readMinutes}M`,
        step: steps.map((text, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: text.split(/(?<=[.:])\s/)[0].slice(0, 80),
            text
        }))
    }
}

export const collectionSchema = (input: {
    name: string
    description: string
    path: string
    posts: PostCard[]
}): Json => {
    const pageUrl = absoluteUrl(input.path)

    return {
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: input.name,
        description: input.description,
        isPartOf: { '@id': WEBSITE_ID },
        inLanguage: 'en-US',
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: input.posts.map((post, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: absoluteUrl(`/${post.pillarSlug}/${post.slug}`),
                name: post.title
            }))
        }
    }
}

const htmlToText = (html: string): string =>
    html
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim()

const exercisePath = (exercise: ExerciseCard): string =>
    `/exercises/${exercise.muscle.slug}/${exercise.slug}`

// An exercise page is a how-to: its numbered steps become HowToStep items.
// Returns null when the steps field has no list, so no empty HowTo is emitted.
export const exerciseHowToSchema = (exercise: ExerciseDetail, equipmentLabel: string): Json | null => {
    const list = /<ol[^>]*>([\s\S]*?)<\/ol>/i.exec(exercise.steps ?? '')
    if (!list) return null

    const steps = [...list[1].matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
        .map((match) => htmlToText(match[1]))
        .filter(Boolean)

    if (steps.length < 2) return null

    const pageUrl = absoluteUrl(exercisePath(exercise))
    const graph: Json = {
        '@type': 'HowTo',
        '@id': `${pageUrl}#howto`,
        name: `How to do the ${exercise.name}`,
        description: exercise.summary,
        inLanguage: 'en-US',
        publisher: { '@id': ORG_ID },
        step: steps.map((text, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: text.split(/(?<=[.:])\s/)[0].slice(0, 80),
            text
        }))
    }

    if (exercise.equipment !== 'bodyweight') {
        graph.tool = [{ '@type': 'HowToTool', name: equipmentLabel }]
    }
    if (exercise.image) {
        graph.image = absoluteUrl(exercise.image)
    }

    return graph
}

export const exerciseCollectionSchema = (input: {
    name: string
    description: string
    path: string
    exercises: ExerciseCard[]
}): Json => {
    const pageUrl = absoluteUrl(input.path)

    return {
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: input.name,
        description: input.description,
        isPartOf: { '@id': WEBSITE_ID },
        inLanguage: 'en-US',
        mainEntity: {
            '@type': 'ItemList',
            numberOfItems: input.exercises.length,
            itemListElement: input.exercises.map((exercise, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: absoluteUrl(exercisePath(exercise)),
                name: exercise.name
            }))
        }
    }
}
